"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

import { Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { cn } from "@/lib/utils";

export interface PlatformFeatureScrollItem {
  icon?: React.ReactNode;
  visual?: React.ReactNode;
  title: string;
  description: React.ReactNode;
  /** Optional longer copy (block content such as paragraphs) shown in an expandable "Read more". */
  details?: React.ReactNode;
  href?: string;
  linkLabel?: string;
}

function FeatureDetails({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <details className={cn("group", className)}>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-accent [&::-webkit-details-marker]:hidden">
        <span className="group-open:hidden">Read more</span>
        <span className="hidden group-open:inline">Show less</span>
        <ChevronDown
          className="h-4 w-4 transition-transform duration-200 group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-text-secondary [&_a]:text-primary [&_a]:underline">
        {children}
      </div>
    </details>
  );
}

interface PlatformFeaturesScrollSectionProps {
  id?: string;
  sectionLabel?: string;
  title: string;
  subtitle?: React.ReactNode;
  features: PlatformFeatureScrollItem[];
  /** Optional call to action rendered below the features. */
  cta?: {
    href: string;
    label: string;
    target?: string;
  };
  className?: string;
}

export const PlatformFeaturesScrollSection: React.FC<PlatformFeaturesScrollSectionProps> = ({
  id,
  sectionLabel,
  title,
  subtitle,
  features,
  cta,
  className = "",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section id={id} className={cn("section-default bg-white", className)}>
      <div className="container mx-auto px-8 xl:px-0">
        {/* Section header — primary page signal */}
        <div className="mb-16 lg:mb-20 max-w-3xl mx-auto text-center">
          {sectionLabel ? <span className="section-label">{sectionLabel}</span> : null}
          <Typography variant="h2" as="h2" className="mb-3 text-4xl font-bold tracking-tight text-text-primary">
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body" as="p" className="max-w-2xl mx-auto text-text-secondary">
              {subtitle}
            </Typography>
          ) : null}
        </div>

        {/* Mobile — individual stacked cards, one per feature, nothing hidden behind tabs */}
        <div className="flex flex-col gap-4 lg:hidden">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            >
              <div className="p-6">
                <div className="flex items-center gap-3.5">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-white text-primary shadow-sm"
                    aria-hidden="true"
                  >
                    {feature.icon}
                  </span>
                </div>
                <Typography variant="h3" as="h3" className="mt-4 text-xl font-bold leading-tight text-text-primary">
                  {feature.title}
                </Typography>
                <span className="mt-3 mb-4 block h-1 w-12 rounded-full bg-primary" aria-hidden="true" />
                <Typography
                  variant="body"
                  as="p"
                  className="text-[15px] font-normal leading-relaxed text-text-secondary [&_a]:underline"
                >
                  {feature.description}
                </Typography>
                {feature.details ? <FeatureDetails className="mt-4">{feature.details}</FeatureDetails> : null}
                {feature.href && feature.linkLabel ? (
                  <Link
                    href={feature.href}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all hover:gap-2.5"
                  >
                    {feature.linkLabel}
                  </Link>
                ) : null}
              </div>
              {feature.visual ? (
                <div className="flex h-[220px] w-full items-center justify-center overflow-hidden border-t border-gray-100 bg-primary/2 p-6 [&_img]:h-auto [&_img]:w-auto [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain">
                  {feature.visual}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Desktop — sidebar rail + featured panel */}
        <div className="relative hidden overflow-hidden rounded-2xl border border-primary/10 bg-linear-to-b from-primary/2 to-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-12px_rgba(16,24,40,0.12)] lg:block">
          {/* Decorative background illustration */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {/* Organic blob shapes — right side, behind the visual */}
            <svg
              className="absolute right-0 top-0 h-full w-[62%] text-primary"
              viewBox="0 0 600 520"
              fill="none"
              preserveAspectRatio="xMaxYMid slice"
            >
              <path
                d="M612 -40C540 20 500 120 520 210C540 300 470 360 380 380C300 398 260 470 300 560L680 560L680 -60Z"
                fill="currentColor"
                opacity="0.05"
              />
              <path
                d="M660 60C560 90 520 180 545 270C568 352 500 420 410 452C345 475 340 545 380 600L720 600L720 20Z"
                fill="currentColor"
                opacity="0.04"
              />
            </svg>
            {/* Soft glow blobs */}
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
            <div className="absolute -bottom-28 right-1/3 h-64 w-64 rounded-full bg-secondary/5 blur-3xl" />
            {/* Dotted grid — top right */}
            <div
              className="absolute right-6 top-8 h-40 w-56 text-primary/15"
              style={{
                backgroundImage: "radial-gradient(currentColor 1.4px, transparent 1.4px)",
                backgroundSize: "16px 16px",
                maskImage: "radial-gradient(120% 120% at 100% 0%, #000 30%, transparent 75%)",
                WebkitMaskImage: "radial-gradient(120% 120% at 100% 0%, #000 30%, transparent 75%)",
              }}
            />
            {/* Dotted grid — bottom left */}
            <div
              className="absolute bottom-6 left-1/3 h-36 w-48 text-primary/12"
              style={{
                backgroundImage: "radial-gradient(currentColor 1.4px, transparent 1.4px)",
                backgroundSize: "16px 16px",
                maskImage: "radial-gradient(120% 120% at 0% 100%, #000 25%, transparent 70%)",
                WebkitMaskImage: "radial-gradient(120% 120% at 0% 100%, #000 25%, transparent 70%)",
              }}
            />
          </div>
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
            {/* Nav rail — icon + title, quiet until selected */}
            <div
              role="tablist"
              aria-label={`${title} — feature list`}
              className="flex gap-1 overflow-x-auto scrollbar-hide border-b border-primary/10 bg-white/60 p-3 lg:flex-col lg:gap-0 lg:overflow-visible lg:border-b-0 lg:border-r lg:border-primary/10 lg:p-0 lg:divide-y lg:divide-primary/10 justify-center"
            >
              {features.map((feature, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={feature.title}
                    id={`feature-tab-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`feature-panel-${index}`}
                    onClick={() => setActiveIndex(index)}
                    className={`group relative flex w-full shrink-0 items-center gap-3.5 rounded-xl border-l-[3px] px-3.5 py-4 text-left transition-colors duration-200 lg:rounded-none lg:px-5 ${
                      isActive
                        ? "border-l-primary bg-primary/6"
                        : "border-l-transparent hover:bg-primary/3"
                    }`}
                  >
                    {/* Icon tile */}
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200 ${
                        isActive
                          ? "border-primary/25 bg-white text-primary shadow-sm"
                          : "border-gray-200 bg-white text-text-secondary/70 group-hover:border-gray-300 group-hover:text-text-primary"
                      }`}
                      aria-hidden="true"
                    >
                      {feature.icon}
                    </span>
                    <span
                      className={`text-sm leading-snug ${
                        isActive ? "font-semibold text-primary" : "font-normal text-text-primary/90 group-hover:text-text-primary"
                      }`}
                    >
                      {feature.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Featured panel — pill + title + copy on the left, visual floats right */}
            <div className="relative min-w-0 p-8 lg:p-10 xl:p-12">
              {features.map((feature, index) => {
                const isActive = index === activeIndex;
                return (
                  <div
                    key={feature.title}
                    id={`feature-panel-${index}`}
                    role="tabpanel"
                    aria-labelledby={`feature-tab-${index}`}
                    aria-hidden={!isActive}
                    inert={!isActive ? true : undefined}
                    className={`grid grid-cols-1 items-center gap-8 transition-opacity duration-300 ease-out lg:grid-cols-[minmax(0,1fr)_minmax(300px,1.05fr)] lg:gap-10 ${
                      isActive
                        ? "relative opacity-100"
                        : "pointer-events-none absolute inset-0 p-8 opacity-0 lg:p-10 xl:p-12"
                    }`}
                  >
                    <div className="order-2 lg:order-1">
                      <Typography
                        variant="h3"
                        as="h3"
                        className="max-w-[16ch] text-[1.75rem] font-bold leading-[1.15] tracking-tight text-text-primary lg:text-[2rem]"
                      >
                        {feature.title}
                      </Typography>
                      <span className="mt-4 mb-6 block h-1 w-12 rounded-full bg-primary" aria-hidden="true" />
                      <Typography
                        variant="body"
                        as="p"
                        className="max-w-[44ch] text-[15px] font-normal leading-relaxed text-text-secondary [&_a]:underline"
                      >
                        {feature.description}
                      </Typography>
                      {feature.details ? (
                        <FeatureDetails className="mt-5 max-w-[44ch]">{feature.details}</FeatureDetails>
                      ) : null}
                      {feature.href && feature.linkLabel ? (
                        <Link
                          href={feature.href}
                          className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all hover:gap-2.5"
                        >
                          {feature.linkLabel}
                        </Link>
                      ) : null}
                    </div>

                    {feature.visual ? (
                      <div className="order-1 flex h-[260px] w-full items-center justify-center overflow-hidden sm:h-[300px] lg:order-2 lg:h-[380px] [&_img]:h-auto [&_img]:w-auto [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain">
                        {feature.visual}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {cta ? (
          <div className="mt-12 flex justify-center">
            <Link
              href={cta.href}
              target={cta.target ?? (cta.href.startsWith("http") ? "_blank" : undefined)}
              rel={cta.href.startsWith("http") ? "noreferrer" : undefined}
            >
              <NewButton variant="primary" size="md" arrow="grow">
                {cta.label}
              </NewButton>
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
};
